<template>
  <div class="distributor-applications-table">
    <div class="table-scroll">
      <div class="table-surface">
        <AppTable class="table" disable-overflow>
          <thead>
            <tr>
              <AppTableSortableHeaderCell :field="submittedAtSortField" :sort="model.sort">
                Inkom
              </AppTableSortableHeaderCell>
              <th scope="col">Kund</th>
              <th scope="col">Handläggare</th>
              <th scope="col">Typ</th>
              <th scope="col">Status</th>
              <th scope="col">Vem har bollen</th>
              <AppTableSortableHeaderCell :field="workResponsibilitySinceSortField" :sort="model.sort">
                Bollen sedan
              </AppTableSortableHeaderCell>
              <th scope="col">Ansökt belopp</th>
              <th scope="col">Godkänt belopp</th>
              <th class="align-center icon-column" scope="col">Prio</th>
              <th class="align-center icon-column" scope="col">Oläst</th>
              <th class="align-center icon-column" scope="col">Konv.</th>
              <th class="align-center icon-column" scope="col">Kompl.</th>
              <th scope="col">ID</th>
            </tr>
          </thead>

          <tbody>
            <AppTableRow v-if="model.loading">
              <AppTableCell :colspan="columnCount.toString()" class="align-center">Hämtar ansökningar...</AppTableCell>
            </AppTableRow>

            <template v-else-if="model.items.length">
              <AppTableRow v-for="item in model.items" :key="item.id" :route-location="applicationRoute(item)">
                <AppTableCell nowrap>
                  {{ formatSubmittedAt(item) }}
                </AppTableCell>

                <AppTableCell>
                  {{ formatMainApplicant(item) }}
                </AppTableCell>

                <AppTableCell>
                  {{ getPersonnelNameByUserId(item.brokerPersonnelUserId) || '-' }}
                </AppTableCell>

                <AppTableCell>
                  <AppChip color="muted">
                    {{ translateType(item.type) }}
                  </AppChip>
                </AppTableCell>

                <AppTableCell>
                  <AppChip :color="getApplicationLifecycleStatusChipColor(item.status)" dot>
                    {{ translateStatus(item.status) }}
                  </AppChip>
                </AppTableCell>

                <AppTableCell>
                  <AppChip v-if="item.workResponsibilityParty" color="muted">
                    {{ WORK_RESPONSIBILITY_PARTY_LABELS[item.workResponsibilityParty] }}
                  </AppChip>
                  <span v-else>-</span>
                </AppTableCell>

                <AppTableCell nowrap>
                  {{ formatWorkResponsibilitySince(item) }}
                </AppTableCell>

                <AppTableCell>
                  {{ formatAmountWithCurrency(item.amountAppliedFor) }}
                </AppTableCell>

                <AppTableCell>
                  {{ formatAmountWithCurrency(item.amountApproved) }}
                </AppTableCell>

                <AppTableCell class="align-center icon-column">
                  <span v-if="item.isHighPrio" class="indicator" title="Hög prio">
                    <SvgIcon :path="mdiAlertCircle" color="orange" size="18" />
                  </span>
                  <span v-else class="empty-indicator">-</span>
                </AppTableCell>

                <AppTableCell class="align-center icon-column">
                  <span v-if="item.hasUnreadMessages" class="indicator" title="Olästa meddelanden">
                    <SvgIcon :path="mdiMessageBadge" color="red" size="18" />
                  </span>
                  <span v-else class="empty-indicator">-</span>
                </AppTableCell>

                <AppTableCell class="align-center icon-column">
                  <span v-if="item.hasOpenConversations" class="indicator" title="Öppen konversation">
                    <SvgIcon :path="mdiMessageOutline" color="blue" size="18" />
                  </span>
                  <span v-else class="empty-indicator">-</span>
                </AppTableCell>

                <AppTableCell class="align-center icon-column">
                  <span v-if="item.hasOpenRequiredAdditions" class="indicator" title="Öppna kompletteringar">
                    <SvgIcon :path="mdiClipboardTextClockOutline" color="green" size="18" />
                  </span>
                  <span v-else class="empty-indicator">-</span>
                </AppTableCell>

                <AppTableCell>
                  {{ item.publicId }}
                </AppTableCell>
              </AppTableRow>
            </template>

            <AppTableRow v-else>
              <AppTableCell :colspan="columnCount.toString()" class="align-center">
                Inga ansökningar hittades.
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>

        <AppTablePaginationFooter :pagination="model.pagination" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { mdiAlertCircle, mdiClipboardTextClockOutline, mdiMessageBadge, mdiMessageOutline } from '@mdi/js'
import { formatAmountWithCurrency, formatDateTimeValue, getApplicationLifecycleStatusChipColor } from '@staff/shared'
import {
  AppChip,
  AppTable,
  AppTableCell,
  AppTablePaginationFooter,
  AppTableRow,
  AppTableSortableHeaderCell,
  SvgIcon,
} from '@staff/ui'

import type {
  DistributorApplicationListSortField,
  DistributorApplicationsTableModel,
} from '@/composables/distributorApplicationSearch'
import type { DistributorApplicationListItem } from '@/services/application'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'
import { applicationRouteLocation } from '@/router/applicationRouteLocations'
import { translateApplicationEnum } from '@/utils/applicationDisplay'

import { WORK_RESPONSIBILITY_PARTY_LABELS } from '@staff/shared'

defineProps<{
  model: DistributorApplicationsTableModel
}>()

const { t } = useI18n()
const { getPersonnelNameByUserId } = usePersonnelDirectory()

const columnCount = 14
const submittedAtSortField: DistributorApplicationListSortField = 'submitted_at'
const workResponsibilitySinceSortField: DistributorApplicationListSortField = 'work_responsibility_since'

const applicationRoute = (application: DistributorApplicationListItem) =>
  applicationRouteLocation({ id: application.id, type: application.type, status: application.status })

const formatSubmittedAt = (application: DistributorApplicationListItem) => formatDateTimeValue(application.submittedAt)

const formatWorkResponsibilitySince = (application: DistributorApplicationListItem) =>
  formatDateTimeValue(application.workResponsibilitySince)

const formatMainApplicant = (application: DistributorApplicationListItem) => {
  return [application.mainApplicant.givenName, application.mainApplicant.surname].filter(Boolean).join(' ') || '-'
}

const translateType = (type?: string) => translateApplicationEnum(t, 'APPLICATION_TYPE', type)
const translateStatus = (status?: string) => translateApplicationEnum(t, 'APPLICATION_STATUS', status)
</script>

<style scoped lang="scss">
.distributor-applications-table {
  margin-bottom: 12px;
}

.table-scroll {
  max-width: 100%;
  overflow-x: auto;
}

.table-surface {
  display: inline-block;
  min-width: 100%;
}

.table {
  width: 100%;
  margin-bottom: 0;
  max-width: 100%;
}

.align-center {
  text-align: center;

  :deep(> a) {
    justify-content: center;
  }
}

.icon-column {
  width: 1%;
  min-width: 64px;
}

.indicator {
  display: inline-flex;
}

.empty-indicator {
  color: var(--color-fg-muted);
}
</style>
